import React, { useState, useEffect, useMemo } from 'react';
import { Download, AlertCircle, Link as LinkIcon, X, CheckCircle2, Search, RefreshCw, ChevronDown } from 'lucide-react';
import { getWooCommerceProducts, getWooCommerceConfig } from '../../services/woocommerce';
import { WooCommerceConfig } from '../../types/integrations';
import { WooCommerceProduct } from '../../types/woocommerce';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { toast, ToastContainer } from 'react-toastify';

interface WooCommerceProductImportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ImportStatus {
  created: number;
  linked: number;
  total: number;
}

async function convertImageToBase64(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = function() {
      const reader = new FileReader();
      reader.onloadend = function() {
        resolve(reader.result as string);
      };
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.readAsDataURL(xhr.response);
    };
    
    xhr.onerror = function() {
      // Tentar com proxy CORS se falhar
      const corsProxy = 'https://api.allorigins.win/raw?url=';
      const proxyUrl = corsProxy + encodeURIComponent(imageUrl);
      
      const xhrProxy = new XMLHttpRequest();
      xhrProxy.onload = function() {
        const reader = new FileReader();
        reader.onloadend = function() {
          resolve(reader.result as string);
        };
        reader.onerror = () => resolve(''); // Retorna vazio em caso de erro
        reader.readAsDataURL(xhrProxy.response);
      };
      
      xhrProxy.onerror = () => resolve(''); // Retorna vazio em caso de erro
      
      xhrProxy.open('GET', proxyUrl);
      xhrProxy.responseType = 'blob';
      xhrProxy.send();
    };
    
    xhr.open('GET', imageUrl);
    xhr.responseType = 'blob';
    xhr.send();
  });
}

// Função para processar imagens em lotes
async function processImageBatch(images: any[], startIndex: number, batchSize: number): Promise<string[]> {
  const batch = images.slice(startIndex, startIndex + batchSize);
  const promises = batch.map(img => convertImageToBase64(img.src));
  const results = await Promise.allSettled(promises);
  
  return results.map(result => 
    result.status === 'fulfilled' ? result.value : ''
  );
}

function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export default function WooCommerceProductImport({ isOpen, onClose, onSuccess }: WooCommerceProductImportProps) {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<WooCommerceProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [config, setConfig] = useState<WooCommerceConfig | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatus>({ created: 0, linked: 0, total: 0 });
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [importedProductIds, setImportedProductIds] = useState<Set<number>>(new Set());
  const { currentUser } = useAuth();

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products.filter(p => !importedProductIds.has(p.id));
    const searchLower = searchTerm.toLowerCase();
    return products
      .filter(p => !importedProductIds.has(p.id))
      .filter(product => 
        product.name.toLowerCase().includes(searchLower) ||
        (product.sku && product.sku.toLowerCase().includes(searchLower))
      );
  }, [products, searchTerm, importedProductIds]);

  useEffect(() => {
    if (isOpen && currentUser?.companyId) {
      loadConfig();
    }
  }, [isOpen, currentUser]);

  const loadConfig = async () => {
    if (!currentUser?.companyId) return;
    
    try {
      const config = await getWooCommerceConfig(currentUser.companyId);
      if (!config) {
        setError('Configuração do WooCommerce não encontrada. Configure na página de integrações.');
        return;
      }
      setConfig(config);
      await loadProducts(config);
    } catch (err) {
      console.error('Error loading WooCommerce config:', err);
      setError('Erro ao carregar configuração do WooCommerce');
    }
  };

  const loadProducts = async (config: WooCommerceConfig, page: number = 1) => {
    try {
      setLoading(page === 1);
      setLoadingMore(page > 1);
      
      const { products: newProducts, totalPages } = await getWooCommerceProducts(config, page);
      
      setProducts(prev => page === 1 ? newProducts : [...prev, ...newProducts]);
      setTotalPages(totalPages);
      setCurrentPage(page);

      // Load existing products to mark as imported
      if (page === 1) {
        await loadExistingProducts();
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Erro ao carregar produtos do WooCommerce');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadExistingProducts = async () => {
    if (!currentUser?.companyId) return;

    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('companyId', '==', currentUser.companyId),
        where('woocommerce.woocommerce_id', '!=', null)
      );
      
      const snapshot = await getDocs(q);
      const importedIds = new Set(
        snapshot.docs.map(doc => {
          const data = doc.data();
          return data.woocommerce?.woocommerce_id;
        }).filter(Boolean)
      );
      
      setImportedProductIds(importedIds);
    } catch (err) {
      console.error('Error loading existing products:', err);
    }
  };

  const loadMore = async () => {
    if (!config || currentPage >= totalPages) return;
    await loadProducts(config, currentPage + 1);
  };

  const findExistingProduct = async (sku: string, ean: string | null) => {
    if (!currentUser?.companyId) return null;

    const productsRef = collection(db, 'products');
    let q;

    if (ean) {
      q = query(
        productsRef,
        where('companyId', '==', currentUser.companyId),
        where('ean', '==', ean)
      );
      const eanSnapshot = await getDocs(q);
      if (!eanSnapshot.empty) {
        return eanSnapshot.docs[0];
      }
    }

    if (sku) {
      q = query(
        productsRef,
        where('companyId', '==', currentUser.companyId),
        where('sku', '==', sku)
      );
      const skuSnapshot = await getDocs(q);
      if (!skuSnapshot.empty) {
        return skuSnapshot.docs[0];
      }
    }

    return null;
  };

  const toggleProduct = (productId: number) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleAllProducts = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleImport = async () => {
    if (!currentUser?.companyId || !config) {
      setError('Configuração não encontrada');
      return;
    }
    
    if (selectedProducts.size === 0) {
      setError('Selecione pelo menos um produto para importar');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);
    setProgress(0);
    setImportStatus({ created: 0, linked: 0, total: selectedProducts.size });

    try {
      const selectedProductsList = products.filter(p => selectedProducts.has(p.id));
      const isAdmin = currentUser.role === 'admin';
      const targetCollection = isAdmin ? 'products' : 'pendingProducts';
      
      // Processa os produtos em lotes de 5 para evitar sobrecarga
      const batchSize = 5;
      for (let i = 0; i < selectedProductsList.length; i += batchSize) {
        const batch = selectedProductsList.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (product) => {
          try {
            // Verifica se o produto já existe
            const existingProduct = await findExistingProduct(
              product.sku || '',
              product.meta_data?.find(m => m.key === '_ean')?.value || null
            );

            if (existingProduct) {
              // Atualiza o produto existente
              await updateDoc(existingProduct.ref, {
                woocommerce: {
                  woocommerce_id: product.id,
                  last_synced: new Date().toISOString()
                },
                updatedAt: new Date().toISOString()
              });

              setImportStatus(prev => ({ ...prev, linked: prev.linked + 1 }));
            } else {
              // Cria um novo produto
              const productData = {
                companyId: currentUser.companyId,
                name: product.name,
                description: stripHtml(product.description || ''),
                sku: product.sku || '',
                regularPrice: parseFloat(product.regular_price || product.price || '0'),
                stockQuantity: product.stock_quantity || 0,
                stockStatus: product.stock_status || 'instock',
                categories: product.categories?.map(cat => cat.name) || [],
                mainImage: product.images?.[0]?.src || '',
                images: product.images?.map(img => img.src) || [],
                weight: parseFloat(product.weight || '0'),
                length: parseFloat(product.dimensions?.length || '0'),
                width: parseFloat(product.dimensions?.width || '0'),
                height: parseFloat(product.dimensions?.height || '0'),
                woocommerce: {
                  woocommerce_id: product.id,
                  last_synced: new Date().toISOString()
                },
                createdAt: new Date(),
                createdBy: currentUser.uid,
                status: isAdmin ? 'approved' : 'pending'
              };

              await addDoc(collection(db, targetCollection), productData);
              setImportStatus(prev => ({ ...prev, created: prev.created + 1 }));
            }
          } catch (err) {
            console.error('Error importing product:', err);
          }
        }));

        // Atualiza o progresso
        setProgress(((i + batch.length) / selectedProductsList.length) * 100);
      }

      setSuccess(true);
      toast.success(isAdmin 
        ? 'Produtos importados com sucesso!' 
        : 'Produtos enviados para aprovação. Aguarde a revisão do administrador.');
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error importing products:', err);
      setError('Erro ao importar produtos');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Importar Produtos do WooCommerce</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4 flex-1 overflow-auto">
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {!config ? (
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
              <p className="text-gray-600">
                Configure o WooCommerce na página de integrações antes de importar produtos.
              </p>
            </div>
          ) : loading && products.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-gray-600">Carregando produtos...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Nenhum produto encontrado no WooCommerce.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedProducts.size === filteredProducts.length}
                    onChange={toggleAllProducts}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm text-gray-600">
                    Selecionar todos ({filteredProducts.length})
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Procurar por nome ou SKU..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary w-64"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  </div>
                  <span className="text-sm text-gray-600">
                    {selectedProducts.size} produto(s) selecionado(s)
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProducts.has(product.id)}
                      onChange={() => toggleProduct(product.id)}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium">{product.name}</h3>
                      <div className="text-sm text-gray-600 flex gap-4">
                        <span>SKU: {product.sku || 'N/A'}</span>
                        <span>Preço: €{parseFloat(product.price || product.regular_price || '0').toFixed(2)}</span>
                        <span>Stock: {product.stock_quantity || 0}</span>
                        {product.sale_price && (
                          <span className="text-green-600">
                            Promoção: €{parseFloat(product.sale_price).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredProducts.length === 0 && searchTerm && (
                <div className="text-center py-4 text-gray-600">
                  Nenhum produto encontrado com o termo "{searchTerm}"
                </div>
              )}

              {currentPage < totalPages && (
                <div className="mt-4 text-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 mx-auto"
                  >
                    {loadingMore ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Carregando mais produtos...
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Carregar mais produtos
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {loading && importStatus.total > 0 && (
            <div className="space-y-2">
              <div className="h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-primary rounded-full transition-all"
                  style={{
                    width: `${progress}%`
                  }}
                />
              </div>
              <div className="text-sm text-gray-600 flex justify-between">
                <span>Progresso: {progress}%</span>
                <span>
                  Importados: {importStatus.created} | Atualizados: {importStatus.linked} | 
                  Total: {importStatus.total}
                </span>
              </div>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded">
              <CheckCircle2 size={16} />
              Importação concluída com sucesso!
            </div>
          )}
        </div>

        <div className="p-4 border-t">
          <button
            onClick={handleImport}
            disabled={loading || selectedProducts.size === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#C5D86D] text-white rounded hover:bg-[#b3c561] disabled:opacity-50"
          >
            {loading ? (
              <>
                <Download size={20} className="animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Download size={20} />
                Importar {selectedProducts.size} Produto(s)
              </>
            )}
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
